import { Fragment } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { usePwaInstall } from "../contexts/PwaInstallContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Share, PlusSquare, CheckCircle2, X, Smartphone } from "lucide-react";

/**
 * Three-in-one PWA install UI, mounted globally from App.js:
 *  • InstallSuccessToast — green ✅ shown briefly on `appinstalled`
 *  • InstallBanner       — smart first-visit floating banner (7-day cooldown)
 *  • IosInstallGuide     — modal with the Share → Add-to-Home-Screen steps
 *  • FooterInstallButton — the compact chip embedded in <Footer />
 * They all read from `usePwaInstall()` so behavior stays consistent
 * across surfaces.
 */

export function InstallSuccessToast() {
  const { t } = useTranslation();
  const { showSuccessToast, dismissSuccessToast } = usePwaInstall();
  if (!showSuccessToast) return null;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[92%] bg-emerald-600 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in"
      role="status"
      data-testid="pwa-install-success-toast"
    >
      <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
      <p className="text-sm font-medium flex-1">{t("pwa.installedSuccess")}</p>
      <button
        onClick={dismissSuccessToast}
        className="hover:bg-white/10 rounded-lg p-1 -mr-1"
        aria-label={t("pwa.close")}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function InstallBanner() {
  const { t } = useTranslation();
  const { bannerEligible, promptInstall, dismissBanner, device, installed } = usePwaInstall();
  if (installed || !bannerEligible) return null;
  const handleInstall = async () => {
    const res = await promptInstall();
    // If native prompt unavailable on Chromium (rare), fall back to the
    // iOS guide OR a hint on the desktop menu.
    if (res.outcome === "unavailable") {
      toast.info(
        device.family === "windows" || device.family === "macos"
          ? t("pwa.browserMenuDesktop")
          : t("pwa.browserMenuMobile"),
      );
    }
    dismissBanner();
  };
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[95] w-[94%] max-w-md rounded-2xl shadow-2xl border border-neutral-200 bg-white/95 backdrop-blur-md p-4 animate-in slide-in-from-bottom-4 fade-in"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-terracotta/10 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-terracotta" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-agave leading-tight">
            {t("pwa.bannerTitle")}
          </p>
          <p className="text-xs text-neutral-600 mt-1 leading-snug">
            {t("pwa.bannerBody")}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleInstall}
              className="btn-primary h-8 px-3 text-xs"
              data-testid="pwa-install-banner-cta"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              {t("pwa.installApp")}
            </Button>
            <button
              onClick={dismissBanner}
              className="text-xs text-neutral-500 hover:text-neutral-700 px-2"
              data-testid="pwa-install-banner-dismiss"
            >
              {t("pwa.notNow")}
            </button>
          </div>
        </div>
        <button
          onClick={dismissBanner}
          className="p-1 -m-1 text-neutral-400 hover:text-neutral-700"
          aria-label={t("pwa.close")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function IosInstallGuide() {
  const { t } = useTranslation();
  const { iosGuideOpen, setIosGuideOpen } = usePwaInstall();
  return (
    <Dialog open={iosGuideOpen} onOpenChange={setIosGuideOpen}>
      <DialogContent className="sm:max-w-md" data-testid="pwa-ios-install-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-terracotta" />
            {t("pwa.iosTitle")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-neutral-600 mb-4">
          {t("pwa.iosIntro")}
        </p>

        <ol className="space-y-4">
          <IosStep
            n="1"
            icon={<Share className="w-5 h-5 text-[#1877F2]" />}
            title={t("pwa.iosStep1Title")}
            body={t("pwa.iosStep1Body")}
          />
          <IosStep
            n="2"
            icon={<PlusSquare className="w-5 h-5 text-neutral-800" />}
            title={t("pwa.iosStep2Title")}
            body={t("pwa.iosStep2Body")}
          />
          <IosStep
            n="3"
            icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
            title={t("pwa.iosStep3Title")}
            body={t("pwa.iosStep3Body")}
          />
        </ol>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={() => setIosGuideOpen(false)}
            className="btn-primary"
            data-testid="pwa-ios-guide-close"
          >
            {t("pwa.gotIt")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IosStep({ n, icon, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta font-heading font-bold flex items-center justify-center flex-shrink-0">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {icon}
          <p className="font-semibold text-agave leading-tight">{title}</p>
        </div>
        <p className="text-sm text-neutral-600 mt-0.5 leading-snug">{body}</p>
      </div>
    </li>
  );
}

export function FooterInstallButton() {
  const { t } = useTranslation();
  const { canOffer, installed, device, promptInstall } = usePwaInstall();
  // If already installed → show a subtle confirmation chip (per spec).
  // If we can offer install on this device → show the CTA.
  // Otherwise → render nothing so we don't clutter the footer.
  if (installed) {
    return (
      <p
        className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mt-3"
        data-testid="pwa-footer-installed-chip"
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        {t("pwa.installedChip")}
      </p>
    );
  }
  // iOS Safari never fires beforeinstallprompt, but we DO want to expose
  // the "add to home screen" guide there. So we render the button when
  // either canOffer OR the user is on iOS Safari.
  const showOnIos = device.family === "ios" && device.browser === "safari";
  if (!canOffer && !showOnIos) return null;
  const handleClick = async () => {
    const res = await promptInstall();
    if (res.outcome === "unavailable") {
      toast.info(t("pwa.browserMenuMobile"));
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-2 mt-3 px-3.5 py-2 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-neutral-800 shadow-sm hover:shadow-md transition-all"
      data-testid="pwa-footer-install-btn"
    >
      <Download className="w-4 h-4" />
      {t("pwa.installRivones")}
    </button>
  );
}

export function PwaInstallSurfaces() {
  return (
    <Fragment>
      <InstallBanner />
      <IosInstallGuide />
      <InstallSuccessToast />
    </Fragment>
  );
}
